import { Box } from '@chakra-ui/react'
import {
  UseComboboxState,
  UseComboboxStateChangeOptions,
  useCombobox,
} from 'downshift'
import React, { ReactNode, RefObject, useCallback, useRef } from 'react'
import { perimeterBarAttribute } from '../components/CirclePerimeterBar'

// #123: the filter bar sits inside the results list, so Tab moves focus from
// the field into the list. downshift reads that blur as "leave and pick the
// highlighted result" — the very mis-selection ORG-02 fought with its inline
// button. This hook tells the combobox apart a blur INTO the bar, keeps the
// list open, and closes it when focus finally leaves the bar.
export default function useKeepOpenOnBarFocus(
  inputRef: RefObject<HTMLInputElement>
) {
  const blurIntoBarRef = useRef(false)

  // Runs before downshift's own blur handler (getInputProps chains ours first)
  const handleInputBlur = useCallback((event: React.FocusEvent) => {
    const next = event.relatedTarget as HTMLElement | null
    blurIntoBarRef.current = !!next?.closest(`[${perimeterBarAttribute}]`)
  }, [])

  const keepOpenReducer = useCallback(
    (
      state: UseComboboxState<any>,
      { type, changes }: UseComboboxStateChangeOptions<any>
    ) => {
      if (type === useCombobox.stateChangeTypes.InputBlur) {
        if (blurIntoBarRef.current) {
          blurIntoBarRef.current = false
          return state
        }
      }
      return changes
    },
    []
  )

  const wrapHeader = useCallback(
    (header: ReactNode, closeMenu: () => void) => (
      <Box
        onBlur={(event: React.FocusEvent<HTMLDivElement>) => {
          const next = event.relatedTarget as Node | null
          if (next === inputRef.current) return
          if (next && event.currentTarget.contains(next)) return
          closeMenu()
        }}
        onKeyDown={(event: React.KeyboardEvent) => {
          // Back to the field, list still open: a second Escape there closes
          // it, as downshift does everywhere else.
          if (event.key !== 'Escape') return
          event.stopPropagation()
          inputRef.current?.focus()
        }}
      >
        {header}
      </Box>
    ),
    [inputRef]
  )

  return { handleInputBlur, keepOpenReducer, wrapHeader }
}
